import React, { useState, useEffect, useCallback, useMemo, FC } from 'react';

import { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import { cn } from 'utils/helpers';
import useEmblaCarousel from 'embla-carousel-react';
import { Attachment } from 'utils/types';
import { FileIcon } from 'ui/components/icon';
import { MdFileDownload } from 'react-icons/md';

import { DotButton, PrevButton, NextButton } from './CarouselArrowsDotsButtons';

import '../../../../assets/styles/EmblaCarousel.css';

type PropType = {
  slides: Omit<Attachment, 'source' | 'fileSize'>[];
  options?: EmblaOptionsType;
  showFilename?: boolean;
  onDownload?: (url: string, fileName: string) => void;
};

const EmblaCarousel: React.FC<PropType> = (props) => {
  const { slides, options, showFilename = false, onDownload } = props;
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const fileSelected = useMemo(() => {
    return slides[selectedIndex];
  }, [selectedIndex, slides]);

  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi],
  );
  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi],
  );
  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi],
  );

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  return (
    <>
      <div className="embla">
        {onDownload && (
          <button
            onClick={() =>
              onDownload(fileSelected.fileUrl, fileSelected.fileName)
            }
            className={cn(
              'transition',
              'absolute',
              'right-5 top-5 z-50',
              'rounded-full',
              'grid place-items-center',
              'w-8 h-8',
              'bg-info-base hover:opacity-80',
            )}
          >
            <MdFileDownload className="text-fill-tertiary w-4 h-4" />
          </button>
        )}
        <div className="embla__viewport" ref={emblaRef}>
          <div className="embla__container">
            {slides.map((file, index) => (
              <div className="embla__slide" key={index}>
                <CarouselItemPreview file={file} />
              </div>
            ))}
          </div>
        </div>

        <PrevButton onClick={scrollPrev} disabled={prevBtnDisabled} />
        <NextButton onClick={scrollNext} disabled={nextBtnDisabled} />

        <div className="embla__dots">
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={index}
              onClick={() => scrollTo(index)}
              className={cn(
                'embla__dot',
                index === selectedIndex ? ' embla__dot--selected' : '',
              )}
            />
          ))}
        </div>
        {showFilename && (
          <div className="p-2 bg-placeholder text-center text-fill-tertiary text-sm w-full">
            {fileSelected?.fileName}
          </div>
        )}
      </div>
    </>
  );
};

export default EmblaCarousel;

type CarouselItemPreviewProps = {
  file: Omit<Attachment, 'source' | 'fileSize'>;
};

const CarouselItemPreview: FC<CarouselItemPreviewProps> = ({ file }) => {
  if (file.fileType === 'application/pdf') {
    return (
      <iframe
        src={`${file.fileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
        width="100%"
        height={500}
      />
    );
  }

  if (file.fileThumbnail && file.fileType.startsWith('image')) {
    return (
      <img
        className="embla__slide__img"
        src={file.fileUrl}
        alt="Your alt text"
      />
    );
  }

  return (
    <div
      className={cn(
        'text-center',
        'space-y-[14px]',
        'w-max',
        'flex',
        'justify-center',
        'flex-col',
        'items-center',
      )}
    >
      <div className="w-16 h-16 bg-fill-primary rounded-md grid place-items-center">
        <FileIcon className="w-7 h-7 text-content" />
      </div>
      <div className="text-fill-tertiary">No Preview Available</div>
    </div>
  );
};
